import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SearchIcon, ZapIcon, ShieldIcon, CodeIcon, DollarSignIcon, UsersIcon, LayoutGridIcon, SparklesIcon, LockIcon, TrendingUpIcon, CheckCircleIcon, ArrowRightIcon, GlobeIcon, CreditCardIcon, RocketIcon, FolderIcon, ShareIcon, WrenchIcon } from 'lucide-react';
type Tab = 'discover' | 'build' | 'use';
export const Marketing = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<Tab>('discover');
  const stats = [{
    label: 'Active Apps',
    value: '10,000+'
  }, {
    label: 'Developers',
    value: '50,000+'
  }, {
    label: 'Monthly Users',
    value: '2M+'
  }, {
    label: 'AI Models',
    value: '15+'
  }];
  return <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50" data-id="element-1647">
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-br from-indigo-600 via-blue-600 to-cyan-600" data-id="element-1648">
        <div className="absolute inset-0 bg-grid-white/[0.05] bg-[size:20px_20px]" data-id="element-1649" />
        <div className="relative max-w-7xl mx-auto px-6 py-20 sm:py-24" data-id="element-1650">
          <div className="text-center" data-id="element-1651">
            <h1 className="text-5xl sm:text-6xl font-bold text-white mb-6 tracking-tight" data-id="element-1652">
              The AI App Platform
              <br data-id="element-1653" />
              <span className="text-cyan-200" data-id="element-1654">for Everyone</span>
            </h1>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto mb-8" data-id="element-1655">
              Discover, build, and use AI applications without the complexity.
              One platform, unlimited possibilities.
            </p>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto mt-12" data-id="element-1656">
              {stats.map(stat => <div key={stat.label} className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20" data-id="element-1657">
                  <div className="text-3xl font-bold text-white mb-1" data-id="element-1658">
                    {stat.value}
                  </div>
                  <div className="text-sm text-blue-100" data-id="element-1659">{stat.label}</div>
                </div>)}
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="sticky top-0 z-40 bg-white border-b border-gray-200 shadow-sm" data-id="element-1660">
        <div className="max-w-7xl mx-auto px-6" data-id="element-1661">
          <div className="flex justify-center space-x-1 py-4" data-id="element-1662">
            {[{
            id: 'discover',
            label: 'Discover Apps',
            icon: SearchIcon
          }, {
            id: 'build',
            label: 'Build Apps',
            icon: CodeIcon
          }, {
            id: 'use',
            label: 'Use Apps',
            icon: ZapIcon
          }].map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id as Tab)} className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-medium transition-all ${activeTab === tab.id ? 'bg-indigo-600 text-white shadow-md' : 'text-gray-600 hover:bg-gray-100'}`} data-id="element-1663">
                <tab.icon className="h-5 w-5" data-id="element-1664" />
                <span data-id="element-1665">{tab.label}</span>
              </button>)}
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="max-w-7xl mx-auto px-6 py-16" data-id="element-1666">
        {/* Discover Tab */}
        {activeTab === 'discover' && <div className="space-y-20" data-id="element-1667">
            {/* Hero */}
            <div className="text-center max-w-3xl mx-auto" data-id="element-1668">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-2xl mb-6" data-id="element-1669">
                <SearchIcon className="h-8 w-8 text-blue-600" data-id="element-1670" />
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-4" data-id="element-1671">
                Explore Thousands of AI Apps
              </h2>
              <p className="text-xl text-gray-600 mb-8" data-id="element-1672">
                From image generation to code optimization, find the perfect AI
                tool for any task. No setup required.
              </p>
              <button onClick={() => navigate('/')} className="inline-flex items-center px-8 py-4 bg-indigo-600 text-white rounded-xl font-semibold hover:bg-indigo-700 transition-colors shadow-lg hover:shadow-xl" data-id="element-1673">
                Browse App Store
                <ArrowRightIcon className="ml-2 h-5 w-5" data-id="element-1674" />
              </button>
            </div>

            {/* Features Grid */}
            <div className="grid md:grid-cols-3 gap-8" data-id="element-1675">
              <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100" data-id="element-1676">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4" data-id="element-1677">
                  <LayoutGridIcon className="h-6 w-6 text-blue-600" data-id="element-1678" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3" data-id="element-1679">
                  Vast Repository
                </h3>
                <p className="text-gray-600" data-id="element-1680">
                  Access 10,000+ AI applications across every category. From
                  productivity to creativity, we have the tools you need.
                </p>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100" data-id="element-1681">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mb-4" data-id="element-1682">
                  <CreditCardIcon className="h-6 w-6 text-green-600" data-id="element-1683" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3" data-id="element-1684">
                  One Credit System
                </h3>
                <p className="text-gray-600" data-id="element-1685">
                  Use credits across all apps. No more juggling multiple
                  accounts, subscriptions, or API keys for different AI
                  services.
                </p>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100" data-id="element-1686">
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mb-4" data-id="element-1687">
                  <RocketIcon className="h-6 w-6 text-purple-600" data-id="element-1688" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3" data-id="element-1689">
                  Instant Access
                </h3>
                <p className="text-gray-600" data-id="element-1690">
                  Start using any app immediately. No complex setup, no
                  configuration. Just click and create.
                </p>
              </div>
            </div>

            {/* Visual Showcase */}
            <div className="bg-gradient-to-br from-blue-600 to-indigo-600 rounded-3xl p-12 text-white" data-id="element-1691">
              <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-1692">
                <div data-id="element-1693">
                  <h3 className="text-3xl font-bold mb-4" data-id="element-1694">
                    Discover Your Next Favorite Tool
                  </h3>
                  <ul className="space-y-4" data-id="element-1695">
                    <li className="flex items-start" data-id="element-1696">
                      <CheckCircleIcon className="h-6 w-6 mr-3 flex-shrink-0 mt-0.5" data-id="element-1697" />
                      <span className="text-blue-50" data-id="element-1698">
                        Browse by category or search for specific capabilities
                      </span>
                    </li>
                    <li className="flex items-start" data-id="element-1699">
                      <CheckCircleIcon className="h-6 w-6 mr-3 flex-shrink-0 mt-0.5" data-id="element-1700" />
                      <span className="text-blue-50" data-id="element-1701">
                        Read reviews and ratings from real users
                      </span>
                    </li>
                    <li className="flex items-start" data-id="element-1702">
                      <CheckCircleIcon className="h-6 w-6 mr-3 flex-shrink-0 mt-0.5" data-id="element-1703" />
                      <span className="text-blue-50" data-id="element-1704">
                        Try apps instantly with our unified credit system
                      </span>
                    </li>
                    <li className="flex items-start" data-id="element-1705">
                      <CheckCircleIcon className="h-6 w-6 mr-3 flex-shrink-0 mt-0.5" data-id="element-1706" />
                      <span className="text-blue-50" data-id="element-1707">
                        Save favorites to your personal library
                      </span>
                    </li>
                  </ul>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20" data-id="element-1708">
                  <div className="space-y-3" data-id="element-1709">
                    <div className="h-12 bg-white/20 rounded-lg animate-pulse" data-id="element-1710" />
                    <div className="grid grid-cols-3 gap-3" data-id="element-1711">
                      <div className="h-24 bg-white/20 rounded-lg animate-pulse" data-id="element-1712" />
                      <div className="h-24 bg-white/20 rounded-lg animate-pulse" data-id="element-1713" />
                      <div className="h-24 bg-white/20 rounded-lg animate-pulse" data-id="element-1714" />
                    </div>
                    <div className="h-32 bg-white/20 rounded-lg animate-pulse" data-id="element-1715" />
                  </div>
                </div>
              </div>
            </div>
          </div>}

        {/* Build Tab */}
        {activeTab === 'build' && <div className="space-y-20" data-id="element-1716">
            {/* Hero */}
            <div className="text-center max-w-3xl mx-auto" data-id="element-1717">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-2xl mb-6" data-id="element-1718">
                <CodeIcon className="h-8 w-8 text-green-600" data-id="element-1719" />
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-4" data-id="element-1720">
                Build & Monetize AI Apps in Minutes
              </h2>
              <p className="text-xl text-gray-600 mb-8" data-id="element-1721">
                No code required. Use our AI-powered builder to create, deploy,
                and monetize applications instantly.
              </p>
              <button onClick={() => navigate('/create')} className="inline-flex items-center px-8 py-4 bg-green-600 text-white rounded-xl font-semibold hover:bg-green-700 transition-colors shadow-lg hover:shadow-xl" data-id="element-1722">
                Start Building
                <ArrowRightIcon className="ml-2 h-5 w-5" data-id="element-1723" />
              </button>
            </div>

            {/* Features Grid */}
            <div className="grid md:grid-cols-2 gap-8" data-id="element-1724">
              <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100" data-id="element-1725">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4" data-id="element-1726">
                  <SparklesIcon className="h-6 w-6 text-blue-600" data-id="element-1727" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3" data-id="element-1728">
                  AI-Powered Builder
                </h3>
                <p className="text-gray-600 mb-4" data-id="element-1729">
                  Describe your app idea in plain English. Our AI helps you
                  build it through a conversational interface.
                </p>
                <ul className="space-y-2 text-sm text-gray-600" data-id="element-1730">
                  <li className="flex items-center" data-id="element-1731">
                    <CheckCircleIcon className="h-4 w-4 mr-2 text-green-600" data-id="element-1732" />
                    Chat-based app creation
                  </li>
                  <li className="flex items-center" data-id="element-1733">
                    <CheckCircleIcon className="h-4 w-4 mr-2 text-green-600" data-id="element-1734" />
                    Instant preview and testing
                  </li>
                  <li className="flex items-center" data-id="element-1735">
                    <CheckCircleIcon className="h-4 w-4 mr-2 text-green-600" data-id="element-1736" />
                    One-click deployment
                  </li>
                </ul>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100" data-id="element-1737">
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mb-4" data-id="element-1738">
                  <GlobeIcon className="h-6 w-6 text-purple-600" data-id="element-1739" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3" data-id="element-1740">
                  Choose Any AI Model
                </h3>
                <p className="text-gray-600 mb-4" data-id="element-1741">
                  Plug into GPT-4, Claude, Gemini, or any other AI model. Switch
                  between models with a single click.
                </p>
                <ul className="space-y-2 text-sm text-gray-600" data-id="element-1742">
                  <li className="flex items-center" data-id="element-1743">
                    <CheckCircleIcon className="h-4 w-4 mr-2 text-green-600" data-id="element-1744" />
                    15+ AI models supported
                  </li>
                  <li className="flex items-center" data-id="element-1745">
                    <CheckCircleIcon className="h-4 w-4 mr-2 text-green-600" data-id="element-1746" />
                    Easy model comparison
                  </li>
                  <li className="flex items-center" data-id="element-1747">
                    <CheckCircleIcon className="h-4 w-4 mr-2 text-green-600" data-id="element-1748" />
                    Automatic fallbacks
                  </li>
                </ul>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100" data-id="element-1749">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mb-4" data-id="element-1750">
                  <DollarSignIcon className="h-6 w-6 text-green-600" data-id="element-1751" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3" data-id="element-1752">
                  Built-in Monetization
                </h3>
                <p className="text-gray-600 mb-4" data-id="element-1753">
                  Set your price and start earning. We handle payments,
                  distribution, and analytics.
                </p>
                <ul className="space-y-2 text-sm text-gray-600" data-id="element-1754">
                  <li className="flex items-center" data-id="element-1755">
                    <CheckCircleIcon className="h-4 w-4 mr-2 text-green-600" data-id="element-1756" />
                    Flexible pricing models
                  </li>
                  <li className="flex items-center" data-id="element-1757">
                    <CheckCircleIcon className="h-4 w-4 mr-2 text-green-600" data-id="element-1758" />
                    Instant payouts
                  </li>
                  <li className="flex items-center" data-id="element-1759">
                    <CheckCircleIcon className="h-4 w-4 mr-2 text-green-600" data-id="element-1760" />
                    Revenue analytics
                  </li>
                </ul>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100" data-id="element-1761">
                <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center mb-4" data-id="element-1762">
                  <WrenchIcon className="h-6 w-6 text-orange-600" data-id="element-1763" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3" data-id="element-1764">
                  No Code Required
                </h3>
                <p className="text-gray-600 mb-4" data-id="element-1765">
                  Build professional apps without writing a single line of code.
                  Simple forms and visual tools.
                </p>
                <ul className="space-y-2 text-sm text-gray-600" data-id="element-1766">
                  <li className="flex items-center" data-id="element-1767">
                    <CheckCircleIcon className="h-4 w-4 mr-2 text-green-600" data-id="element-1768" />
                    Visual app builder
                  </li>
                  <li className="flex items-center" data-id="element-1769">
                    <CheckCircleIcon className="h-4 w-4 mr-2 text-green-600" data-id="element-1770" />
                    Pre-built templates
                  </li>
                  <li className="flex items-center" data-id="element-1771">
                    <CheckCircleIcon className="h-4 w-4 mr-2 text-green-600" data-id="element-1772" />
                    Easy API integration
                  </li>
                </ul>
              </div>
            </div>

            {/* CTA Section */}
            <div className="bg-gradient-to-br from-green-600 to-emerald-600 rounded-3xl p-12 text-white text-center" data-id="element-1773">
              <h3 className="text-3xl font-bold mb-4" data-id="element-1774">
                Join 50,000+ Developers Building on RealUseAI
              </h3>
              <p className="text-xl text-green-50 mb-8 max-w-2xl mx-auto" data-id="element-1775">
                From idea to published app in minutes. Start building your AI
                business today.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-1776">
                <button onClick={() => navigate('/create')} className="px-8 py-4 bg-white text-green-600 rounded-xl font-semibold hover:bg-green-50 transition-colors shadow-lg" data-id="element-1777">
                  Create Your First App
                </button>
                <button onClick={() => navigate('/apps')} className="px-8 py-4 bg-green-700 text-white rounded-xl font-semibold hover:bg-green-800 transition-colors border-2 border-white/20" data-id="element-1778">
                  View Examples
                </button>
              </div>
            </div>
          </div>}

        {/* Use Tab */}
        {activeTab === 'use' && <div className="space-y-20" data-id="element-1779">
            {/* Hero */}
            <div className="text-center max-w-3xl mx-auto" data-id="element-1780">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-2xl mb-6" data-id="element-1781">
                <ZapIcon className="h-8 w-8 text-purple-600" data-id="element-1782" />
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-4" data-id="element-1783">
                Your Personal AI Workspace
              </h2>
              <p className="text-xl text-gray-600 mb-8" data-id="element-1784">
                Build custom workflows, organize your favorite apps, and keep
                your data secure. All in one place.
              </p>
              <button onClick={() => navigate('/my-apps')} className="inline-flex items-center px-8 py-4 bg-purple-600 text-white rounded-xl font-semibold hover:bg-purple-700 transition-colors shadow-lg hover:shadow-xl" data-id="element-1785">
                Go to My Apps
                <ArrowRightIcon className="ml-2 h-5 w-5" data-id="element-1786" />
              </button>
            </div>

            {/* Features Grid */}
            <div className="grid md:grid-cols-3 gap-8" data-id="element-1787">
              <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100" data-id="element-1788">
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mb-4" data-id="element-1789">
                  <FolderIcon className="h-6 w-6 text-purple-600" data-id="element-1790" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3" data-id="element-1791">
                  Personal Library
                </h3>
                <p className="text-gray-600" data-id="element-1792">
                  Save and organize your favorite apps. Access them instantly
                  from your personal dashboard.
                </p>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100" data-id="element-1793">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4" data-id="element-1794">
                  <WrenchIcon className="h-6 w-6 text-blue-600" data-id="element-1795" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3" data-id="element-1796">
                  Custom Workflows
                </h3>
                <p className="text-gray-600" data-id="element-1797">
                  Build apps tailored to your specific needs. Modify existing
                  apps or create new ones from scratch.
                </p>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100" data-id="element-1798">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mb-4" data-id="element-1799">
                  <ShareIcon className="h-6 w-6 text-green-600" data-id="element-1800" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3" data-id="element-1801">
                  Share & Collaborate
                </h3>
                <p className="text-gray-600" data-id="element-1802">
                  Share your custom apps with team members or the community.
                  Collaborate on workflows.
                </p>
              </div>
            </div>

            {/* Privacy Section */}
            <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-3xl p-12 text-white" data-id="element-1803">
              <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-1804">
                <div data-id="element-1805">
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-white/10 rounded-xl mb-6" data-id="element-1806">
                    <ShieldIcon className="h-6 w-6 text-white" data-id="element-1807" />
                  </div>
                  <h3 className="text-3xl font-bold mb-4" data-id="element-1808">
                    Your Data, Your Control
                  </h3>
                  <p className="text-slate-300 mb-6" data-id="element-1809">
                    We take privacy seriously. Your data is encrypted,
                    protected, and never shared without your permission.
                  </p>
                  <ul className="space-y-3" data-id="element-1810">
                    <li className="flex items-start" data-id="element-1811">
                      <LockIcon className="h-5 w-5 mr-3 flex-shrink-0 mt-0.5 text-green-400" data-id="element-1812" />
                      <span className="text-slate-200" data-id="element-1813">
                        End-to-end encryption for all data
                      </span>
                    </li>
                    <li className="flex items-start" data-id="element-1814">
                      <LockIcon className="h-5 w-5 mr-3 flex-shrink-0 mt-0.5 text-green-400" data-id="element-1815" />
                      <span className="text-slate-200" data-id="element-1816">
                        Private apps stay private by default
                      </span>
                    </li>
                    <li className="flex items-start" data-id="element-1817">
                      <LockIcon className="h-5 w-5 mr-3 flex-shrink-0 mt-0.5 text-green-400" data-id="element-1818" />
                      <span className="text-slate-200" data-id="element-1819">
                        Full control over data sharing and permissions
                      </span>
                    </li>
                    <li className="flex items-start" data-id="element-1820">
                      <LockIcon className="h-5 w-5 mr-3 flex-shrink-0 mt-0.5 text-green-400" data-id="element-1821" />
                      <span className="text-slate-200" data-id="element-1822">
                        GDPR and SOC 2 compliant
                      </span>
                    </li>
                  </ul>
                </div>
                <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10" data-id="element-1823">
                  <div className="space-y-4" data-id="element-1824">
                    <div className="flex items-center justify-between p-4 bg-white/10 rounded-lg" data-id="element-1825">
                      <span className="text-sm" data-id="element-1826">Data Encryption</span>
                      <CheckCircleIcon className="h-5 w-5 text-green-400" data-id="element-1827" />
                    </div>
                    <div className="flex items-center justify-between p-4 bg-white/10 rounded-lg" data-id="element-1828">
                      <span className="text-sm" data-id="element-1829">Private by Default</span>
                      <CheckCircleIcon className="h-5 w-5 text-green-400" data-id="element-1830" />
                    </div>
                    <div className="flex items-center justify-between p-4 bg-white/10 rounded-lg" data-id="element-1831">
                      <span className="text-sm" data-id="element-1832">Access Controls</span>
                      <CheckCircleIcon className="h-5 w-5 text-green-400" data-id="element-1833" />
                    </div>
                    <div className="flex items-center justify-between p-4 bg-white/10 rounded-lg" data-id="element-1834">
                      <span className="text-sm" data-id="element-1835">Compliance Ready</span>
                      <CheckCircleIcon className="h-5 w-5 text-green-400" data-id="element-1836" />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Features List */}
            <div className="grid md:grid-cols-2 gap-8" data-id="element-1837">
              <div className="space-y-6" data-id="element-1838">
                <h3 className="text-2xl font-bold text-gray-900" data-id="element-1839">
                  Productivity Features
                </h3>
                <div className="space-y-4" data-id="element-1840">
                  <div className="flex items-start" data-id="element-1841">
                    <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0" data-id="element-1842">
                      <CheckCircleIcon className="h-5 w-5 text-purple-600" data-id="element-1843" />
                    </div>
                    <div data-id="element-1844">
                      <h4 className="font-semibold text-gray-900 mb-1" data-id="element-1845">
                        Quick Access Dashboard
                      </h4>
                      <p className="text-gray-600 text-sm" data-id="element-1846">
                        All your apps in one place with smart organization
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start" data-id="element-1847">
                    <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0" data-id="element-1848">
                      <CheckCircleIcon className="h-5 w-5 text-purple-600" data-id="element-1849" />
                    </div>
                    <div data-id="element-1850">
                      <h4 className="font-semibold text-gray-900 mb-1" data-id="element-1851">
                        Usage History
                      </h4>
                      <p className="text-gray-600 text-sm" data-id="element-1852">
                        Track your interactions and revisit previous work
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start" data-id="element-1853">
                    <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0" data-id="element-1854">
                      <CheckCircleIcon className="h-5 w-5 text-purple-600" data-id="element-1855" />
                    </div>
                    <div data-id="element-1856">
                      <h4 className="font-semibold text-gray-900 mb-1" data-id="element-1857">
                        Custom Categories
                      </h4>
                      <p className="text-gray-600 text-sm" data-id="element-1858">
                        Organize apps your way with tags and folders
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-6" data-id="element-1859">
                <h3 className="text-2xl font-bold text-gray-900" data-id="element-1860">
                  Customization Options
                </h3>
                <div className="space-y-4" data-id="element-1861">
                  <div className="flex items-start" data-id="element-1862">
                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0" data-id="element-1863">
                      <CheckCircleIcon className="h-5 w-5 text-blue-600" data-id="element-1864" />
                    </div>
                    <div data-id="element-1865">
                      <h4 className="font-semibold text-gray-900 mb-1" data-id="element-1866">
                        Modify Any App
                      </h4>
                      <p className="text-gray-600 text-sm" data-id="element-1867">
                        Fork and customize existing apps to fit your needs
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start" data-id="element-1868">
                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0" data-id="element-1869">
                      <CheckCircleIcon className="h-5 w-5 text-blue-600" data-id="element-1870" />
                    </div>
                    <div data-id="element-1871">
                      <h4 className="font-semibold text-gray-900 mb-1" data-id="element-1872">
                        Build Private Apps
                      </h4>
                      <p className="text-gray-600 text-sm" data-id="element-1873">
                        Create apps just for yourself or your team
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start" data-id="element-1874">
                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0" data-id="element-1875">
                      <CheckCircleIcon className="h-5 w-5 text-blue-600" data-id="element-1876" />
                    </div>
                    <div data-id="element-1877">
                      <h4 className="font-semibold text-gray-900 mb-1" data-id="element-1878">
                        Workflow Automation
                      </h4>
                      <p className="text-gray-600 text-sm" data-id="element-1879">
                        Chain apps together for powerful workflows
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>}
      </div>

      {/* Final CTA */}
      <div className="bg-gradient-to-br from-indigo-600 via-blue-600 to-cyan-600 py-20" data-id="element-1880">
        <div className="max-w-4xl mx-auto px-6 text-center" data-id="element-1881">
          <h2 className="text-4xl font-bold text-white mb-6" data-id="element-1882">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-blue-100 mb-8" data-id="element-1883">
            Join millions of users discovering, building, and using AI apps on
            RealUseAI
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-1884">
            <button onClick={() => navigate('/')} className="px-8 py-4 bg-white text-indigo-600 rounded-xl font-semibold hover:bg-blue-50 transition-colors shadow-lg" data-id="element-1885">
              Explore Apps
            </button>
            <button onClick={() => navigate('/create')} className="px-8 py-4 bg-indigo-700 text-white rounded-xl font-semibold hover:bg-indigo-800 transition-colors border-2 border-white/20" data-id="element-1886">
              Start Building
            </button>
          </div>
        </div>
      </div>
    </div>;
};